'use client'

import { I18nextProvider } from 'react-i18next'
import i18n from '@/lib/i18n'
import { useLanguageStore } from '@/lib/store'
import { useHydration } from '@/hooks/useHydration'
import { useEffect, useState } from 'react'

// RTL languages list
const RTL_LANGUAGES = ['he', 'ar', 'fa']

interface I18nProviderProps {
  children: React.ReactNode
}

export function I18nProvider({ children }: I18nProviderProps) {
  const { currentLanguage } = useLanguageStore()
  const [isHydrated, setIsHydrated] = useState(false)
  
  // Handle hydration
  useHydration()

  useEffect(() => {
    // Wait for hydration to complete
    const timer = setTimeout(() => {
      setIsHydrated(true)
    }, 100)

    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    // Set the language from store when component mounts and is hydrated
    if (isHydrated && currentLanguage && i18n.language !== currentLanguage) {
      i18n.changeLanguage(currentLanguage)
    }
  }, [currentLanguage, isHydrated])

  // Set document direction and language attribute based on current language
  useEffect(() => {
    if (isHydrated && currentLanguage) {
      const isRtl = RTL_LANGUAGES.includes(currentLanguage)
      document.documentElement.dir = isRtl ? 'rtl' : 'ltr'
      document.documentElement.lang = currentLanguage
    }
  }, [currentLanguage, isHydrated])

  return (
    <I18nextProvider i18n={i18n}>
      {children}
    </I18nextProvider>
  )
}
